import {
  ListRequest,
  useVbsCertificateListQuery,
} from "@/services/backend/vbs/certificates/api/certificate-api";
import { rfc3339toDateString } from "@/shared/lib/utilities/date";
import { ReactNode } from "react";
import { Badge } from "@/shared/components/ui/badge";
import t from "@/lang/lang";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Edit } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Certificate } from "@/services/backend/vbs/certificates/types/certificate";
import { TableCard } from "@/shared/components/tables/table-card";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";

export function CertificateListTable({
  request,
  onRequestChange,
  renderRowLink,
  hidePagination = false,
  hideBorder = false,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  renderRowLink: (certificateId: string, children: ReactNode) => ReactNode;
  hidePagination?: boolean;
  hideBorder?: boolean;
}) {
  const { data: list, isLoading, error } = useVbsCertificateListQuery(request);

  const emptyList = !isLoading && !error && list!.items.length === 0;

  return (
    <div className="grid gap-2">
      <TableCard className={hideBorder ? "border-none shadow-none" : ""}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Status")}</TableHead>
              <TableHead>{t("Arbeitsbeginn")}</TableHead>
              <TableHead>{t("Arbeitsende")}</TableHead>
              <TableHead>{t("Ausstellung")}</TableHead>
              <TableHead>{t("Aktionen")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={5} />}
            {error && <TableRtkErrorRow error={error} colSpan={5} />}
            {emptyList && (
              <TableEmptyRow
                colSpan={5}
                text={t("Keine Zertifikate gefunden")}
              />
            )}
            {list &&
              list.items.map((certificate) => (
                <CertificateTableRow
                  key={certificate.id}
                  certificate={certificate}
                  renderRowLink={renderRowLink}
                />
              ))}
          </TableBody>
        </Table>
      </TableCard>
      {!hidePagination && list && !emptyList && (
        <PaginationLimitOffset
          offset={request.offset ?? 0}
          limit={request.limit ?? 0}
          totalCount={list.totalCount}
          setLimitOffset={(limit, offset) =>
            onRequestChange({ ...request, limit, offset })
          }
          className="ml-auto"
        />
      )}
    </div>
  );
}

function CertificateTableRow({
  certificate,
  renderRowLink,
}: {
  certificate: Certificate;
  renderRowLink: (certificateId: string, children: ReactNode) => ReactNode;
}) {
  return (
    <TableRow>
      <TableCell>
        <CertificateStatusBadge issued={certificate.immutable} />
      </TableCell>
      <TableCell>{rfc3339toDateString(certificate.workStartedAt)}</TableCell>
      <TableCell>{rfc3339toDateString(certificate.workEndedAt)}</TableCell>
      <TableCell>{issueDate(certificate.issueDate)}</TableCell>
      <TableCell>
        {renderRowLink(
          certificate.id,
          <Button variant="outline" size="sm">
            <Edit className="h-4 w-4" />
          </Button>,
        )}
      </TableCell>
    </TableRow>
  );
}

export function CertificateTableFilters({
  request,
  onRequestChange,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <OrderBySelect<string>
        orderBy={request.orderBy}
        onOrderByChange={(orderBy) => onRequestChange({ ...request, orderBy })}
        fields={[
          { label: t("Ausstellung"), value: "issueDate" },
          { label: t("Erstellt am"), value: "createdAt" },
          { label: t("Aktualisiert am"), value: "updatedAt" },
        ]}
      />
    </div>
  );
}

export function useDefaultVbsCertificateListRequest(
  processId: string,
): ListRequest {
  return {
    process: { active: true, values: [processId] },
    orderBy: [{ field: "createdAt" }],
    limit: 50,
    offset: 0,
  };
}

function CertificateStatusBadge(props: { issued: boolean }) {
  const { issued } = props;
  if (issued) {
    return <Badge className="bg-green-600">{t("Ausgestellt")}</Badge>;
  }
  return (
    <Badge className="text-foreground bg-yellow-400">
      {t("In Bearbeitung")}
    </Badge>
  );
}

function issueDate(date: string): string {
  const dateString = rfc3339toDateString(date);
  if (dateString === "") {
    return "Nicht ausgestellt";
  }
  return dateString;
}
